import React from 'react';
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  useTheme,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  overflowY: 'auto',
}));

/**
 * A reusable drawer component that can be used throughout the application
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls the drawer's open state
 * @param {function} props.onClose - Function to call when drawer should close
 * @param {string} props.title - Title to display in drawer header
 * @param {React.ReactNode} props.children - Content to render inside drawer
 * @param {string} [props.anchor='right'] - Drawer anchor position ('left', 'right', 'top', 'bottom')
 * @param {string} [props.width='400px'] - Drawer width (for left/right anchors)
 * @param {string} [props.height='400px'] - Drawer height (for top/bottom anchors)
 * @param {Object} [props.headerProps] - Additional props for header component
 * @param {Object} [props.contentProps] - Additional props for content wrapper
 */
const AppDrawer = ({
  open,
  onClose,
  title,
  children,
  anchor = 'right',
  width = '400px',
  height = '400px',
  headerProps = {},
  contentProps = {},
}) => {
  const theme = useTheme();

  const getDrawerStyle = () => {
    const style = {
      '& .MuiDrawer-paper': {
        backgroundColor: theme.palette.background.paper,
      },
    };

    if (anchor === 'left' || anchor === 'right') {
      style['& .MuiDrawer-paper'].width = width;
    } else {
      style['& .MuiDrawer-paper'].height = height;
    }

    return style;
  };

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      sx={getDrawerStyle()}
    >
      <DrawerHeader {...headerProps}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close drawer"
        >
          <CloseIcon />
        </IconButton>
      </DrawerHeader>
      <DrawerContent {...contentProps}>
        {children}
      </DrawerContent>
    </Drawer>
  );
};

export default AppDrawer;
